'use client';

import { resetPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
export const resetPasswordForm = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(6, VM.MIN_LENGTH_VALIDATION_MESSAGE(6)),
      password_confirmation: __eliadmin_zod_1().min(6, VM.MIN_LENGTH_VALIDATION_MESSAGE(6)),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

export function ResetPasswordForm({ token }: { token: string }) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof resetPasswordForm>>>({
    resolver: zodResolver(resetPasswordForm(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
      password_confirmation: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await resetPassword({ ...data, token, locale });
      // todo: redirect to login page
      toast({
        title: t('text649'),
        variant: 'success',
      });
      router.push(ROUTES.SIGN_IN());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text651'),
        description: t('text3140'),
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col space-y-6', 'ea-reset-password-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className="ea-reset-password-form__header text-center md:text-start">
          <h3 className={cn('text-2xl font-bold', 'ea-reset-password-form__header-title')}>
            {t('text1696')}
          </h3>
        </div>

        {/* content */}
        <div className={cn('flex flex-col space-y-4', 'ea-reset-password-form__content')}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem required className="ea-reset-password-form__field-email">
                <FormLabel className="ea-reset-password-form__label">{t('text35')}</FormLabel>
                <FormControl className="ea-reset-password-form__control">
                  <Input
                    placeholder={t('text35')}
                    type="email"
                    {...field}
                    className={{ main: cn('', 'ea-reset-password-form__input') }}
                  />
                </FormControl>
                <FormMessage className="ea-reset-password-form__message" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className="ea-reset-password-form__field-password-new">
                <FormLabel className="ea-reset-password-form__label">{t('text191')}</FormLabel>
                <FormControl className="ea-reset-password-form__control">
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                      className={{ main: cn('', 'ea-reset-password-form__input') }}
                    />

                    <button
                      type="button"
                      onMouseDown={handlePasswordMouseDown}
                      onMouseUp={handlePasswordMouseUp}
                      onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                      onTouchStart={handlePasswordMouseDown}
                      onTouchEnd={handlePasswordMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="ea-reset-password-form__message" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem required className="ea-reset-password-form__field-password-new-repeat">
                <FormLabel className="ea-reset-password-form__label">{t('text1108')}</FormLabel>
                <FormControl className="ea-reset-password-form__control">
                  <div className="relative">
                    <Input
                      placeholder={t('text1108')}
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      {...field}
                      className={{ main: cn('', 'ea-reset-password-form__input') }}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordConfirmationMouseDown}
                      onMouseUp={handlePasswordConfirmationMouseUp}
                      onMouseLeave={handlePasswordConfirmationMouseUp} // In case the cursor leaves the button area while pressing
                      onTouchStart={handlePasswordConfirmationMouseDown}
                      onTouchEnd={handlePasswordConfirmationMouseUp}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="ea-reset-password-form__message" />
              </FormItem>
            )}
          />
        </div>

        {/* FOOTER */}
        <div
          className={cn('flex flex-col items-start space-y-4', 'ea-reset-password-form__footer')}
        >
          <Button
            type="submit"
            className={cn('w-full', 'ea-reset-password-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text187')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
